import { useState } from 'react';
import { api, ApiError } from '../lib/api';

type UpgradeVariant =
  | 'trial'
  | 'past_due'
  | 'expired'
  | 'readonly'
  | 'limit';

interface UpgradeModalProps {
  variant: UpgradeVariant;
  graceDaysLeft?: number;
  daysUntilReadOnly?: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const TIERS: {
  tier: 'starter' | 'standard' | 'pro';
  price: string;
  calendars: number;
  label: string;
}[] = [
  {
    tier: 'starter',
    price: '$1',
    calendars: 1,
    label: 'Starter',
  },
  {
    tier: 'standard',
    price: '$2',
    calendars: 3,
    label: 'Standard',
  },
  { tier: 'pro', price: '$3', calendars: 10, label: 'Pro' },
];

export default function UpgradeModal({
  variant,
  graceDaysLeft,
  daysUntilReadOnly,
  onClose,
  onSuccess,
}: UpgradeModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = async (
    tier: 'starter' | 'standard' | 'pro'
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const { url } = await api.billing.checkout(tier);
      window.location.href = url;
    } catch (err) {
      setIsLoading(false);
      setError(
        err instanceof ApiError
          ? `Checkout failed: ${err.code}`
          : 'Failed to start checkout. Please try again.'
      );
    }
  };

  const handlePortal = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { url } = await api.billing.portal();
      window.location.href = url;
    } catch (err) {
      setIsLoading(false);
      setError(
        err instanceof ApiError
          ? `Failed to open billing portal: ${err.code}`
          : 'Failed to open billing portal. Please try again.'
      );
    }
  };

  const content = {
    trial: {
      icon: '🎉',
      title: 'Start Your Free Trial',
      description:
        'Creating your first shared calendar starts a 90-day free trial. No credit card required.',
      primaryAction: 'Start Free Trial',
      showTiers: false,
      onPrimary: onSuccess || onClose,
      colorClass:
        'bg-blue-50 border-blue-200 text-blue-800',
    },
    past_due: {
      icon: '⚠️',
      title: 'Payment Failed',
      description: graceDaysLeft
        ? `Your subscription payment failed. You have ${graceDaysLeft} days to update your payment method before your account is limited.`
        : 'Your subscription payment failed. Please update your payment method to avoid service interruption.',
      primaryAction: 'Update Payment Method',
      showTiers: false,
      onPrimary: handlePortal,
      colorClass:
        'bg-yellow-50 border-yellow-200 text-yellow-800',
    },
    expired: {
      icon: '🔒',
      title: 'Subscription Expired',
      description: daysUntilReadOnly
        ? `Your trial or subscription has ended. Choose a plan below to keep creating and editing calendars. Your existing calendars will become read-only in ${daysUntilReadOnly} days.`
        : 'Your trial or subscription has ended. Choose a plan below to restore full access to your calendars.',
      showTiers: true,
      primaryAction: 'Choose Plan',
      onPrimary: onClose,
      colorClass: 'bg-red-50 border-red-200 text-red-800',
    },
    readonly: {
      icon: '📖',
      title: 'Read-Only Mode',
      description:
        'Your subscription has been expired for over 30 days. Your non-personal calendars are now read-only. Choose a plan below to restore full editing access.',
      showTiers: true,
      primaryAction: 'Choose Plan',
      onPrimary: onClose,
      colorClass:
        'bg-gray-50 border-gray-200 text-gray-800',
    },
    limit: {
      icon: '📅',
      title: 'Calendar Limit Reached',
      description:
        "You've reached the limit of shared calendars on your current plan. Upgrade to create more.",
      showTiers: true,
      primaryAction: 'Choose Plan',
      onPrimary: onClose,
      colorClass:
        'bg-amber-50 border-amber-200 text-amber-800',
    },
  };

  const currentContent = content[variant];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/40"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="paper-card w-full max-w-md overflow-hidden">
        {/* Header with color accent */}
        <div
          className={`px-6 py-4 border-b ${currentContent.colorClass}`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {currentContent.icon}
            </span>
            <h2 className="font-serif text-xl font-bold">
              {currentContent.title}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-ink-700 leading-relaxed">
            {currentContent.description}
          </p>

          {currentContent.showTiers && (
            <div className="space-y-2">
              {TIERS.map(
                ({ tier, price, calendars, label }) => (
                  <button
                    key={tier}
                    onClick={() => handleCheckout(tier)}
                    disabled={isLoading}
                    className="w-full flex items-center justify-between p-3 rounded border border-paper-300 hover:border-accent hover:bg-paper-100 transition-colors disabled:opacity-50 text-left"
                  >
                    <span className="font-medium text-ink-900">
                      {label}
                    </span>
                    <span className="font-mono text-sm text-ink-600">
                      {price}/month · {calendars}{' '}
                      {calendars === 1
                        ? 'calendar'
                        : 'calendars'}
                    </span>
                  </button>
                )
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="font-mono text-xs text-red-700">
                {error}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-2">
            {!currentContent.showTiers && (
              <button
                onClick={onClose}
                disabled={isLoading}
                className="btn-ghost"
              >
                Cancel
              </button>
            )}
            {!currentContent.showTiers && (
              <button
                onClick={currentContent.onPrimary}
                disabled={isLoading}
                className="btn-primary"
              >
                {isLoading
                  ? 'Loading…'
                  : currentContent.primaryAction}
              </button>
            )}
            {currentContent.showTiers && (
              <button
                onClick={onClose}
                disabled={isLoading}
                className="btn-ghost"
              >
                Not Now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
